/**
 * Aparência do widget, em três cards e não numa lista de doze campos.
 *
 * A ordem antiga era a ordem em que os campos foram sendo escritos: título, subtítulo, imagem,
 * posição, fundo, distâncias, cores, atrasos. Quem procurava "a cor" passava por "a distância", e
 * quem mexia no tema não percebia que ele reescrevia dois campos vinte pixels abaixo.
 *
 * Agora são três responsabilidades, cada uma no seu card: **quem é** (o que aparece escrito no
 * cabeçalho do chat), **como se parece** (tema e cores, juntos, porque um mexe no outro) e **como se
 * comporta** (onde fica na tela e quando abre).
 */

import { useState } from 'react';
import {
  CHAT_SURFACE_LIST,
  DEFAULT_CHAT_BACKGROUND,
  resolveChatSurface,
  type Appearance,
  type ChatBackground,
} from '@merlin/flow-engine';
import { useEditor } from '../../store/editor.js';
import { getDataSource } from '../../data/index.js';
import { Field, Input, Notice, Section, Select } from '../ui.js';
import { IconeEstilo } from '../icons.js';
import { SeloPropagacao } from './Propagacao.js';

const POSITIONS: { value: Appearance['position']; label: string }[] = [
  { value: 'bottom-right', label: 'Inferior direito' },
  { value: 'bottom-left', label: 'Inferior esquerdo' },
  { value: 'top-right', label: 'Superior direito' },
  { value: 'top-left', label: 'Superior esquerdo' },
];

const CORES: { key: 'primaryColor' | 'bubbleColor' | 'textColor'; label: string; hint: string }[] = [
  { key: 'primaryColor', label: 'Marca', hint: 'Botão flutuante, cabeçalho e botão enviar.' },
  { key: 'bubbleColor', label: 'Bolha recebida', hint: 'Fundo da mensagem do bot.' },
  { key: 'textColor', label: 'Texto da bolha', hint: 'Cor do texto do bot.' },
];

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Amostra de cor com o hexadecimal ao lado, editável.
 *
 * Só o seletor nativo não bastava: a cor da marca chega do cliente como texto (`#25D366`), e sem
 * campo para colar era preciso caçá-la no disco de cores. E o hexadecimal visível é o que permite
 * conferir se duas telas estão com o mesmo valor.
 */
function ColorField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange(next: string): void;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <input
          type="color"
          aria-label={`${label} — seletor`}
          value={HEX.test(value) ? value : '#25d366'}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-slate-300"
        />
        <Input
          value={value}
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          className="font-mono"
        />
      </div>
    </Field>
  );
}

export function AppearanceSection() {
  const appearance = useEditor((state) => state.appearance);
  const setAppearance = useEditor((state) => state.setAppearance);
  const id = useEditor((state) => state.id);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File): Promise<void> => {
    if (!id) return;
    setUploading(true);
    setUploadError(null);
    try {
      const url = await getDataSource().uploadButtonImage(id, file);
      setAppearance({ imageUrl: url });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : String(error));
    } finally {
      setUploading(false);
    }
  };

  const tema = resolveChatSurface(appearance.chatBackground ?? DEFAULT_CHAT_BACKGROUND);

  return (
    <div className="space-y-6">
      <Section
        icon={IconeEstilo}
        title="Identidade"
        description="O que o visitante lê no cabeçalho da janela de conversa."
        action={<SeloPropagacao versionado />}
      >
        <Field label="Título">
          <Input value={appearance.title} onChange={(event) => setAppearance({ title: event.target.value })} />
        </Field>
        <Field label="Subtítulo" hint="Costuma dizer o tempo de resposta ou quem atende.">
          <Input
            value={appearance.subtitle ?? ''}
            onChange={(event) => setAppearance({ subtitle: event.target.value })}
          />
        </Field>

        <Field
          label="Imagem do botão"
          hint="URL https. Sem imagem, o widget usa o ícone do WhatsApp embutido."
          error={uploadError}
        >
          <Input
            value={appearance.imageUrl ?? ''}
            placeholder="https://…"
            onChange={(event) => setAppearance({ imageUrl: event.target.value || undefined })}
          />
        </Field>

        {/*
          O `<input type=file>` cru não tem estado, não tem foco visível e o texto do botão é o do
          sistema operacional — em três máquinas, três aparências. Escondido atrás de um `<label>`
          com cara de botão, ele fica igual ao resto do painel e continua alcançável por teclado,
          porque o label é o rótulo do próprio campo.
        */}
        <label
          className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 transition hover:bg-slate-50 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-slate-900 ${
            uploading ? 'pointer-events-none opacity-45' : ''
          }`}
        >
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          {uploading ? 'Enviando…' : 'Enviar uma imagem'}
        </label>
      </Section>

      <Section
        title="Tema da conversa"
        description="O tema define fundo, moldura e o balão enviado. As cores abaixo são o que sobra para a sua marca."
        action={<SeloPropagacao versionado />}
      >
        <Field label="Fundo da conversa">
          <Select
            value={appearance.chatBackground ?? DEFAULT_CHAT_BACKGROUND}
            onChange={(event) => {
              /*
               * O tema traz consigo as cores de bolha e texto que combinam com ele, gravadas num
               * `setAppearance` só — um autosave só. Sem isso, escolher o fundo escuro deixaria a
               * bolha clara de antes por cima dele: legível, mas nada parecido com o WhatsApp. Ficam
               * nos campos abaixo, visíveis e editáveis, em vez de sobrescritas em silêncio na hora
               * de renderizar.
               */
              const escolhido = resolveChatSurface(event.target.value as ChatBackground);
              setAppearance({
                chatBackground: escolhido.id,
                bubbleColor: escolhido.recommendedBubble,
                textColor: escolhido.recommendedText,
              });
            }}
          >
            {CHAT_SURFACE_LIST.map((surface) => (
              <option key={surface.id} value={surface.id}>
                {surface.label}
              </option>
            ))}
          </Select>
        </Field>

        <Notice tone="info">
          Trocar o tema <strong>reescreve</strong> a bolha recebida e o texto dela com os valores que
          combinam com ele — os dois campos abaixo. Ajuste depois, se quiser: nada é sobrescrito de
          novo até você trocar de tema.
        </Notice>

        {/*
          Amostra do tema escolhido. Ela é a única parte desta tela que mostra o resultado sem sair
          dela; para julgar de verdade existe o Preview, mas para "é esse mesmo o escuro?" basta.
        */}
        <div
          className="flex items-center gap-3 rounded-lg border border-slate-200 p-3"
          style={{ background: tema.canvas }}
        >
          <span
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: appearance.bubbleColor, color: appearance.textColor }}
          >
            Mensagem do bot
          </span>
          <span
            className="rounded-lg px-3 py-2 text-sm"
            style={
              tema.outgoing
                ? { background: tema.outgoing.bubble, color: tema.outgoing.text }
                : { background: appearance.primaryColor, color: '#fff' }
            }
          >
            Resposta
          </span>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {CORES.map((cor) => (
            <ColorField
              key={cor.key}
              label={cor.label}
              hint={cor.hint}
              value={appearance[cor.key]}
              onChange={(next) => setAppearance({ [cor.key]: next } as Partial<Appearance>)}
            />
          ))}
        </div>
      </Section>

      <Section
        title="Posição e comportamento"
        description="Onde o botão flutuante fica na página do cliente e quando a conversa começa."
        action={<SeloPropagacao versionado />}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Posição">
            <Select
              value={appearance.position}
              onChange={(event) => setAppearance({ position: event.target.value as Appearance['position'] })}
            >
              {POSITIONS.map((position) => (
                <option key={position.value} value={position.value}>
                  {position.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Distância X (px)">
            <Input
              type="number"
              value={appearance.offsetX}
              onChange={(event) => setAppearance({ offsetX: Number(event.target.value) })}
            />
          </Field>
          <Field label="Distância Y (px)">
            <Input
              type="number"
              value={appearance.offsetY}
              onChange={(event) => setAppearance({ offsetY: Number(event.target.value) })}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Atraso de digitação (ms)" hint="Simula a pausa entre mensagens do bot.">
            <Input
              type="number"
              value={appearance.typingDelayMs}
              onChange={(event) => setAppearance({ typingDelayMs: Number(event.target.value) })}
            />
          </Field>
          <Field label="Abrir sozinho após (ms)" hint="0 desliga.">
            <Input
              type="number"
              value={appearance.autoOpenDelayMs ?? 0}
              onChange={(event) => setAppearance({ autoOpenDelayMs: Number(event.target.value) })}
            />
          </Field>
        </div>
      </Section>
    </div>
  );
}
